<script setup lang="ts">
// src/SideWinder.Web/src/components/StrategyAllocationCard.vue
//
// Multi-strategy allocation Task 4 (spec 2026-07-26-multi-strategy-
// allocation-design.md §6): per-user ON/OFF + weight% control across
// BGA/JH/GOLD. Mounted at the top of ExecPanel for every portfolio
// subdomain (bga/jh/gold); for the gold subdomain it is the ONLY
// dealing-relevant card ExecPanel shows.
//
// GOLD carries a MANDATORY informed-consent step (spec §2 locked decision
// 3): flipping the GOLD toggle on opens an inline arming modal showing the
// live ramp-status (promotion marker, shadow days, holdings-snapshot age,
// runbook link) and requires an explicit Confirm before the row's enabled
// flag actually flips — Cancel leaves it off. There is no way to reach
// enabled=true for a newly-armed GOLD row without going through the modal:
// the checkbox's own v-model is never bound directly, only mutated by
// confirmGoldArm().
import { ref, computed, onMounted, watch } from 'vue'
import { api, type FundAllocation, type GoldRampStatus, type UserExecutionStatus } from '../api/client'
import { useAuth } from '../composables/useAuth'
import { useFundEnablement } from '../composables/useFundEnablement'
import { ALL_FUNDS, GATED_FUNDS, REGISTERED_FUNDS, type Fund } from './nav/fundConfig'
import { splitFundAllocations, potRowsError, tierOrNull, type AllocationModel } from './strategy/allocationModel'

// Linked users Task 9 fix round 1 (reviewer, spec 2026-09-28): this card is
// the Exec panel's ONLY toggles and its only manual-trade buttons (on
// gold/gge, ExecPanel renders NOTHING else — see ExecPanel.vue's
// `v-if="portfolio !== 'gold' && portfolio !== 'gge'"` guard around every
// other card). A viewer must see none of its write controls: fund toggles,
// weight inputs, Save, Liquidate, or the arming Confirm. Since the arming
// modal can only ever open via the checkbox's onCheckboxChange handler
// (never rendered for a viewer below), hiding the checkbox alone also
// removes any path to the arming Confirm — no separate guard needed there.
const { isViewer } = useAuth()

// Fund list comes from the registry (GGE promotion Phase 4) — this card
// renders one row per fund in canonical allocation order.
//
// OTP is excluded (exec-pane cleanup, 2026-09-03): OTP is driven off
// residual cash, not a strategy weight, so it has no place in a strategy
// allocation. Arming/disarming OTP is a superuser-only operation via the
// OTP window / runbook (docs/runbooks/otp-gate.md "Arming a user"). The
// GET still echoes a fifth OTP row — that row is how arming is recorded
// server-side — and normalizeRows() drops it here; the PUT validator
// (ValidateFundAllocations) has never accepted an "OTP" row anyway.
//
// Japan Phase 2 (per-user fund entitlement, spec 2026-09-27, Task 11):
// FUND_ORDER is now a computed that also filters through the shared
// isFundVisible predicate (final-review fix I3): entitlements not loaded ⇒
// gated funds hidden, live funds shown; loaded ⇒ exact filter.
//
// Spec 2026-10-02 §7 (D7): the card has two models, told apart by the GET shape.
//  - legacy:     exactly the card as it was — the four live funds, weights summing to 100.
//  - shared_pot: weights are per-fund entry targets (they may sum above 100), each row also has a
//                Tier and a Reserve %, and JAPAN gets a row when the user is entitled
//                (REGISTERED_FUNDS includes the gated funds; isFundVisible filters them).
const model = ref<AllocationModel>('legacy')
const isSharedPot = computed(() => model.value === 'shared_pot')
const { isFundVisible, hasAccess } = useFundEnablement()
// Charteris Phase 3 (spec 2026-10-05 §7) and AMATI Phase 3 (2026-10-13): every gated fund is in the
// shared pot like JAPAN — a row only when the user is entitled (isFundVisible), so a non-entitled
// user never sees or PUTs one.
const FUND_ORDER = computed<ReadonlyArray<Fund>>(() =>
  (isSharedPot.value ? REGISTERED_FUNDS : ALL_FUNDS)
    .filter(f => f !== 'OTP' && isFundVisible(f)))
// Spec 2026-09-30 §8: rows SHOWN to the user — FUND_ORDER minus funds the admin switched off.
// Hidden rows stay in `rows` (the server-zeroed off/0 values) and ride along unchanged in the PUT,
// which requires BGA/JH/GOLD to be present.
const SHOWN_FUNDS = computed<ReadonlyArray<Fund>>(() => FUND_ORDER.value.filter(f => hasAccess(f)))

// Funds whose enable-toggle requires the informed-consent arming modal
// (spec §2 locked decision 3 — GOLD; extended to GGE at its launch, same
// newer-track-record rationale). Each maps to its live ramp-status fetch.
const ARMED_FUNDS: Partial<Record<Fund, () => Promise<GoldRampStatus>>> = {
  GOLD: api.getGoldRampStatus,
  GGE: api.getGgeRampStatus,
}

const collapsed = ref(false)
const loading = ref(true)
const saving = ref(false)
const saved = ref(false)
const error = ref<string | null>(null)

const rows = ref<FundAllocation[]>(FUND_ORDER.value.map(fund => ({ fund, enabled: false, weightPct: 0 })))
const statuses = ref<Record<Fund, UserExecutionStatus | null>>(
  Object.fromEntries(FUND_ORDER.value.map(f => [f, null])) as Record<Fund, UserExecutionStatus | null>)

const liquidating = ref<Fund | null>(null)
const liquidateResult = ref<string | null>(null)

// Arming modal state — armingFund names which armed fund the open modal is
// for (null = closed). The modal-required path is enforced structurally by
// never letting an armed fund's checkbox itself flip the model to true;
// see onCheckboxChange/confirmArm below.
const armingFund = ref<Fund | null>(null)
const rampStatus = ref<GoldRampStatus | null>(null)
const rampLoading = ref(false)
const rampError = ref<string | null>(null)

// Task 8b (owner-requested, spec 2026-10-02): explanatory (i) panel for the
// shared-pot Tier/Reserve columns. One shared panel toggled by either icon —
// same pattern as ExecPanel.vue's Auto-Execute info-icon/info-panel.
const showPotInfo = ref(false)

// Final-review fix (2026-10-02 item 1): rowByFund must NEVER return undefined
// to the template. The sequence `/fund-allocations` → `/api/auth/me` can land
// in either order; while entitlements are null, FUND_ORDER excludes JAPAN, so
// a shared-pot payload that already contains a JAPAN row used to be dropped by
// normalizeRows and then — when /me arrived and FUND_ORDER regained JAPAN —
// the template loop called rowByFund('JAPAN') on a missing row and the whole
// card blanked. Fixed on two sides: (a) normalizeRows now keeps every fetched
// row, including funds not in FUND_ORDER yet, so JAPAN survives the pre-/me
// window; (b) rowByFund lazily synthesises an off/0 row if one is still
// missing (and pushes it into rows so later writes bind), so a template read
// is defensive even against an ordering we did not anticipate.
function synthRow(fund: Fund): FundAllocation {
  return isSharedPot.value
    ? { fund, enabled: false, weightPct: 0, tier: null, reservePct: 0 }
    : { fund, enabled: false, weightPct: 0 }
}

function rowByFund(fund: Fund): FundAllocation {
  const found = rows.value.find(r => r.fund === fund)
  if (found) return found
  const row = synthRow(fund)
  rows.value.push(row)
  return row
}

// Server responses are merged onto the FUND_ORDER row set so a payload missing
// a fund can never leave rowByFund undefined mid-render — the missing fund
// renders disabled at 0%. Any fetched row for a fund NOT currently in
// FUND_ORDER (e.g. a JAPAN row arriving before entitlements) is appended so
// its weight/tier/reserve survive until the display picks it up (the template
// renders by SHOWN_FUNDS, so appended extras stay invisible until they are
// legitimately shown).
function normalizeRows(fetched: FundAllocation[]): FundAllocation[] {
  const byFund = new Map<string, FundAllocation>()
  for (const r of fetched) if (!byFund.has(r.fund)) byFund.set(r.fund, r)
  const result: FundAllocation[] = []
  for (const fund of FUND_ORDER.value) {
    const found = byFund.get(fund)
    if (!isSharedPot.value) {
      result.push(found ?? { fund, enabled: false, weightPct: 0 })
    } else {
      result.push({
        fund,
        enabled: found?.enabled ?? false,
        weightPct: found?.weightPct ?? 0,
        tier: found?.tier ?? null,
        reservePct: found?.reservePct ?? 0,
      })
    }
    byFund.delete(fund)
  }
  // Carry forward only shared-pot gated-fund rows (JAPAN) that arrived before
  // the entitlement did — the live case this fix exists for. Everything else
  // the server sent (notably OTP) is deliberately dropped here: OTP has never
  // appeared in rows, and legacy mode has no concept of a hidden-but-held row.
  if (isSharedPot.value) {
    for (const r of byFund.values()) {
      if (!(REGISTERED_FUNDS as readonly string[]).includes(r.fund) || r.fund === 'OTP') continue
      // Only funds that are gated (currently JAPAN) can disappear from
      // FUND_ORDER mid-lifetime; non-gated extras indicate a server shape
      // change this file does not yet understand and are ignored.
      if (!(GATED_FUNDS as readonly string[]).includes(r.fund)) continue
      result.push({
        fund: r.fund,
        enabled: !!r.enabled,
        weightPct: r.weightPct ?? 0,
        tier: r.tier ?? null,
        reservePct: r.reservePct ?? 0,
      })
    }
  }
  return result
}

// Re-run normalisation when FUND_ORDER changes (entitlements arrive after the
// GET): any fund that just became visible but isn't yet in rows gets a synth
// off/0 slot so the template render is safe. We re-normalise from the current
// rows so user edits made since load() are preserved.
watch(FUND_ORDER, () => {
  rows.value = normalizeRows(rows.value)
})

const sumPct = computed(() => rows.value.reduce((sum, r) => sum + (Number(r.weightPct) || 0), 0))
const sumValid = computed(() => Math.abs(sumPct.value - 100) <= 0.01)
// Shared pot: no sum rule — only per-row rules (spec §6.2). null = saveable.
const potError = computed(() => (isSharedPot.value ? potRowsError(rows.value) : null))
const canSave = computed(() => (isSharedPot.value ? potError.value === null : sumValid.value))

async function loadStatuses() {
  await Promise.all(SHOWN_FUNDS.value.map(async fund => {
    try {
      statuses.value[fund] = await api.userExecutionStatus(fund)
    } catch {
      statuses.value[fund] = null
    }
  }))
}

async function load() {
  loading.value = true
  error.value = null
  try {
    const fetched = splitFundAllocations(await api.getFundAllocations())
    model.value = fetched.model          // before normalizeRows: FUND_ORDER depends on it
    rows.value = normalizeRows(fetched.rows)
  } catch (e: any) {
    error.value = e.message ?? 'Failed to load fund allocations'
  } finally {
    loading.value = false
  }
  await loadStatuses()
}

function onCheckboxChange(fund: Fund, evt: Event) {
  const target = evt.target as HTMLInputElement
  if (!(fund in ARMED_FUNDS)) {
    rowByFund(fund).enabled = target.checked
    return
  }
  // Armed funds: the checkbox is never bound via v-model — toggling ON
  // reverts the visual state and opens the arming modal instead; toggling
  // OFF (an already-enabled row) applies immediately (no consent needed
  // to disable).
  if (target.checked) {
    target.checked = false
    void openArmModal(fund)
  } else {
    rowByFund(fund).enabled = false
  }
}

// Reviewer round 2: the informed-consent contract requires LIVE ramp data
// — a transient fetch failure must not silently fall back to "Confirm is
// fine". armAcknowledged gates Confirm ONLY on the failure path (reset
// every time the modal opens) so a blip doesn't hard-block a superuser who
// deliberately wants to proceed, while consent is never implicit.
const armAcknowledged = ref(false)

async function openArmModal(fund: Fund) {
  const fetchRamp = ARMED_FUNDS[fund]
  if (!fetchRamp) return
  armingFund.value = fund
  rampStatus.value = null
  rampError.value = null
  armAcknowledged.value = false
  rampLoading.value = true
  try {
    rampStatus.value = await fetchRamp()
  } catch (e: any) {
    rampError.value = e.message ?? 'Failed to load ramp status'
  } finally {
    rampLoading.value = false
  }
}

const armConfirmDisabled = computed(() =>
  rampLoading.value || (!!rampError.value && !armAcknowledged.value)
)

function confirmArm() {
  if (armConfirmDisabled.value || !armingFund.value) return
  rowByFund(armingFund.value).enabled = true
  armingFund.value = null
}

function cancelArm() {
  armingFund.value = null
}

async function save() {
  error.value = null
  if (!isSharedPot.value && !sumValid.value) {
    error.value = `Weights must sum to 100% (currently ${sumPct.value.toFixed(2)}%)`
    return
  }
  if (isSharedPot.value && potError.value) {
    error.value = potError.value
    return
  }
  saving.value = true
  try {
    // rows carries only FUND_ORDER (no OTP row — see the FUND_ORDER note
    // above), and the server PUT carries any armed OTP row forward itself
    // (I1), so the whole row set is the payload. Shared pot: every row also
    // sends its tier (an emptied input is null) and reserve.
    const payload: FundAllocation[] = isSharedPot.value
      // Defence in depth (Charteris Phase 3): a gated row the caller cannot see (not entitled, or
      // /me not loaded yet) is never PUT. The server carries an entitled user's persisted gated row
      // forward when the body omits it, and refuses a non-entitled one — so omitting is always safe.
      ? rows.value.filter(r => !(GATED_FUNDS as readonly string[]).includes(r.fund) || isFundVisible(r.fund)).map(r => ({
          fund: r.fund,
          enabled: r.enabled,
          weightPct: Number(r.weightPct) || 0,
          tier: tierOrNull(r.tier),
          reservePct: Number(r.reservePct ?? 0) || 0,
        }))
      : rows.value
    const result = splitFundAllocations(await api.putFundAllocations(payload))
    model.value = result.model
    rows.value = normalizeRows(result.rows)
    saved.value = true
    setTimeout(() => { saved.value = false }, 2000)
  } catch (e: any) {
    error.value = e.message ?? 'Failed to save fund allocations'
  } finally {
    saving.value = false
  }
}

function statusLabel(fund: Fund): string {
  const s = statuses.value[fund]
  if (!s) return '—'
  switch (s.status) {
    case 'waiting': return 'Waiting'
    case 'executed': return `Executed${s.decision ? ' ' + s.decision : ''}`
    case 'retrying': return 'Retrying'
    case 'hold': return 'Hold'
    case 'missed': return 'Missed'
    case 'no_trade': return 'No trade'
    case 'error': return 'Error'
    default: return 'Idle'
  }
}

function isSameUtcDate(a: Date, b: Date): boolean {
  return a.getUTCFullYear() === b.getUTCFullYear()
    && a.getUTCMonth() === b.getUTCMonth()
    && a.getUTCDate() === b.getUTCDate()
}

// Guard companion to freeze semantics (spec §6): disable a fund's
// liquidate button while today's dealing for that fund is in flight or
// already landed, so a manual liquidate can't race the auto-dealing tick.
// There is no dedicated "idempotence marker" surface exposed to the UI —
// this reads the same per-fund execution-status chip already fetched for
// display and treats waiting/retrying (deal in progress) or an
// executed-today row as the marker.
function isDealtToday(fund: Fund): boolean {
  const s = statuses.value[fund]
  if (!s) return false
  if (s.status === 'waiting' || s.status === 'retrying') return true
  if (s.status === 'executed' && s.executedAt) {
    // Minor reviewer fix: derive "today" from the server payload's OWN
    // updatedAt timestamp (set in the same write as executedAt — see
    // UserExecutionStatusRepository.UpsertAsync) rather than the browser's
    // local clock, so client/server clock skew can't defeat the guard.
    const reference = s.updatedAt ? new Date(s.updatedAt) : new Date()
    return isSameUtcDate(new Date(s.executedAt), reference)
  }
  return false
}

function liquidateDisabled(fund: Fund): boolean {
  if (liquidating.value !== null) return true
  return isDealtToday(fund)
}

async function liquidate(fund: Fund) {
  const confirmed = window.confirm(
    `Sell ALL ${fund} holdings now across every allocated account? This cannot be undone.`
  )
  if (!confirmed) return
  liquidating.value = fund
  error.value = null
  liquidateResult.value = null
  try {
    const result = await api.liquidateFund(fund)
    liquidateResult.value = `${fund}: ${result.results.length} sell attempt(s) submitted`
    await loadStatuses()
  } catch (e: any) {
    error.value = e.message ?? `Failed to liquidate ${fund}`
  } finally {
    liquidating.value = null
  }
}

onMounted(load)

defineExpose({ rows, sumPct, sumValid, model, potError, save, liquidate })
</script>

<template>
  <section class="alloc-card" data-testid="strategy-allocation-card">
    <header class="alloc-header" data-testid="alloc-header" @click="collapsed = !collapsed">
      <h3>Strategy Allocation</h3>
      <button type="button" class="collapse-btn" data-testid="alloc-collapse-toggle" @click.stop="collapsed = !collapsed">
        {{ collapsed ? '▸' : '▾' }}
      </button>
    </header>

    <div v-show="!collapsed" class="alloc-body" data-testid="alloc-body">
      <div v-if="loading" class="alloc-loading">Loading…</div>

      <template v-else>
        <div v-if="isSharedPot" class="alloc-pot-legend" data-testid="alloc-pot-legend">
          <span class="alloc-pot-legend-item">
            Tier
            <span
              class="info-icon"
              data-testid="alloc-tier-info"
              role="button"
              tabindex="0"
              aria-label="Explain tiers"
              :aria-expanded="showPotInfo"
              @click.stop="showPotInfo = !showPotInfo"
              @keydown.enter.prevent="showPotInfo = !showPotInfo"
              @keydown.space.prevent="showPotInfo = !showPotInfo"
            >i</span>
          </span>
          <span class="alloc-pot-legend-item">
            Reserve %
            <span
              class="info-icon"
              data-testid="alloc-reserve-info"
              role="button"
              tabindex="0"
              aria-label="Explain reserves"
              :aria-expanded="showPotInfo"
              @click.stop="showPotInfo = !showPotInfo"
              @keydown.enter.prevent="showPotInfo = !showPotInfo"
              @keydown.space.prevent="showPotInfo = !showPotInfo"
            >i</span>
          </span>
        </div>

        <div v-if="isSharedPot && showPotInfo" class="info-panel" data-testid="alloc-pot-info-panel">
          <p><strong>Tier</strong></p>
          <ul>
            <li>Cash goes to Tier 1 first, then Tier 2, and so on. Funds in the same tier share the available cash pro rata to their weights.</li>
            <li>A fund only buys when its signal is BUY. A fund is never sold to make room for another; it leaves only on its own SELL signal.</li>
          </ul>

          <p><strong>Weight %</strong></p>
          <ul>
            <li>The share of the whole pot a fund aims for when it buys. Weights can add up to more than 100%: the tiers decide who gets cash first.</li>
            <li>Small slices<sup>2</sup> are skipped — a per-account buy under £100 is never placed.</li>
          </ul>

          <p><strong>Reserve %</strong></p>
          <ul>
            <li>When a tier buys, it leaves this % of the whole pot unspent. That keeps cash free so an earlier tier (a lower number) can still buy tomorrow. A tier uses the largest reserve among its switched-on funds.</li>
          </ul>

          <p><strong>Worked example (pot £400,000)</strong></p>
          <ul>
            <li>Monday: JH and Gold are held (£320k). Cash is £80k. Japan (Tier 4) is the only fund on BUY.</li>
            <li>Tuesday: GGE (Tier 2) turns BUY.</li>
          </ul>
          <table class="info-panel-table">
            <thead>
              <tr><th></th><th>Monday</th><th>Tuesday</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Japan reserve 0%</td>
                <td>Japan buys £80k. Cash £0.</td>
                <td>GGE gets nothing. It waits until another fund is sold.</td>
              </tr>
              <tr>
                <td>Japan reserve 10% (£40k)</td>
                <td>Japan buys £40k. £40k stays in cash.</td>
                <td>GGE buys £40k straight away and is topped up later<sup>1</sup>.</td>
              </tr>
            </tbody>
          </table>

          <p>Changes you save here<sup>3</sup> apply at the next 08:40 dealing plan.</p>

          <p>
            <sup>1</sup> Money from a sale can't be spent until the next dealing day (HL settles T+1).<br>
            <sup>2</sup> A buy under £100 is skipped.<br>
            <sup>3</sup> Changes here take effect at the next 08:40 dealing plan.
          </p>
        </div>

        <div class="alloc-row" v-for="fund in SHOWN_FUNDS" :key="fund" :data-testid="`alloc-row-${fund.toLowerCase()}`">
          <label class="alloc-toggle-label" v-if="!isViewer">
            <input
              type="checkbox"
              class="alloc-toggle-input"
              :data-testid="`alloc-toggle-${fund.toLowerCase()}`"
              :checked="rowByFund(fund).enabled"
              @change="onCheckboxChange(fund, $event)"
            />
            <span class="alloc-fund-name">{{ fund }}</span>
          </label>
          <span v-else class="alloc-fund-name alloc-fund-name-readonly" :data-testid="`alloc-fund-readonly-${fund.toLowerCase()}`">
            {{ fund }}: {{ rowByFund(fund).enabled ? 'On' : 'Off' }}
          </span>

          <input
            v-if="!isViewer"
            type="number"
            class="alloc-weight-input"
            :data-testid="`alloc-weight-${fund.toLowerCase()}`"
            v-model.number="rowByFund(fund).weightPct"
            min="0"
            max="100"
            step="0.01"
          />
          <span v-else class="alloc-weight-readonly" :data-testid="`alloc-weight-readonly-${fund.toLowerCase()}`">
            {{ rowByFund(fund).weightPct }}%
          </span>
          <span v-if="!isViewer" class="alloc-weight-pct">%</span>

          <template v-if="isSharedPot">
            <label v-if="!isViewer" class="alloc-pot-field">
              <span class="alloc-pot-label">Tier</span>
              <input
                type="number"
                class="alloc-pot-input"
                :data-testid="`alloc-tier-${fund.toLowerCase()}`"
                v-model.number="rowByFund(fund).tier"
                min="1"
                max="9"
                step="1"
                inputmode="numeric"
              />
            </label>
            <span v-else class="alloc-weight-readonly" :data-testid="`alloc-tier-readonly-${fund.toLowerCase()}`">
              Tier {{ rowByFund(fund).tier ?? '—' }}
            </span>
            <label v-if="!isViewer" class="alloc-pot-field">
              <span class="alloc-pot-label">Reserve</span>
              <input
                type="number"
                class="alloc-pot-input"
                :data-testid="`alloc-reserve-${fund.toLowerCase()}`"
                v-model.number="rowByFund(fund).reservePct"
                min="0"
                max="50"
                step="0.01"
                inputmode="decimal"
              />
              <span class="alloc-weight-pct">%</span>
            </label>
            <span v-else class="alloc-weight-readonly" :data-testid="`alloc-reserve-readonly-${fund.toLowerCase()}`">
              Reserve {{ rowByFund(fund).reservePct ?? 0 }}%
            </span>
          </template>

          <span
            class="alloc-status-chip"
            :class="'status-' + (statuses[fund]?.status ?? 'idle')"
            :data-testid="`alloc-status-${fund.toLowerCase()}`"
          >{{ statusLabel(fund) }}</span>

          <button
            v-if="!isViewer"
            type="button"
            class="btn-liquidate"
            :data-testid="`alloc-liquidate-${fund.toLowerCase()}`"
            :disabled="liquidateDisabled(fund)"
            @click="liquidate(fund)"
          >{{ liquidating === fund ? '…' : 'Liquidate' }}</button>
        </div>

        <div class="alloc-sum-row" v-if="!isViewer">
          <span class="alloc-sum" data-testid="alloc-sum" :class="{ invalid: !canSave }">
            <template v-if="isSharedPot">Entry targets total: {{ sumPct.toFixed(2) }}% (can exceed 100%)</template>
            <template v-else>Total: {{ sumPct.toFixed(2) }}%</template>
          </span>
          <button
            type="button"
            class="btn-save"
            data-testid="alloc-save-btn"
            :disabled="!canSave || saving"
            @click="save"
          >{{ saving ? 'Saving…' : 'Save' }}</button>
          <span v-if="saved" class="saved-badge" data-testid="alloc-saved-badge">Saved</span>
        </div>
        <p v-else class="viewer-note" data-testid="viewer-readonly-note">Read-only account</p>
        <div v-if="isSharedPot && !isViewer && potError" class="alloc-pot-error" data-testid="alloc-pot-error">{{ potError }}</div>
        <p v-if="isSharedPot" class="alloc-pot-hint" data-testid="alloc-pot-hint">
          Shared pot: each weight is that fund's entry target as a % of the pot. Tiers are filled in order
          (1 first) from settled cash; funds in the same tier share the cash pro rata. A reserve applies to
          the whole tier (the highest one wins) and is left unspent.
        </p>

        <div v-if="error" class="alloc-error" data-testid="alloc-error">{{ error }}</div>
        <div v-if="liquidateResult" class="alloc-liquidate-result" data-testid="alloc-liquidate-result">{{ liquidateResult }}</div>
      </template>
    </div>

    <!-- Armed-fund arming modal (spec §2 locked decision 3 — mandatory, no
         bypass). Parameterized over armingFund (GOLD/GGE); data-testids are
         fund-prefixed so existing gold-arming-* selectors keep working. -->
    <div
      v-if="armingFund"
      class="modal-backdrop"
      :data-testid="`${armingFund.toLowerCase()}-arming-modal`"
      tabindex="-1"
      @click.self="cancelArm"
      @keydown.esc="cancelArm"
    >
      <div class="modal">
        <h3 class="modal-title">Enable {{ armingFund }} dealing — informed consent</h3>
        <p class="modal-help">
          {{ armingFund }}'s automated-dealing track record is newer than the
          established funds'. Review the live ramp state below before enabling
          it for your account.
        </p>

        <div v-if="rampLoading" class="modal-loading">Loading ramp status…</div>
        <template v-else-if="rampStatus">
          <div class="modal-field">
            <span class="modal-label">Promoted</span>
            <span :data-testid="`${armingFund.toLowerCase()}-arming-promoted`">{{ rampStatus.promoted ? 'Yes' : 'No' }}</span>
          </div>
          <div class="modal-field">
            <span class="modal-label">Shadow days</span>
            <span :data-testid="`${armingFund.toLowerCase()}-arming-shadow-days`">{{ rampStatus.shadowDays }}</span>
          </div>
          <div class="modal-field">
            <span class="modal-label">Holdings snapshot age</span>
            <span :data-testid="`${armingFund.toLowerCase()}-arming-snapshot-age`">{{ rampStatus.holdingsSnapshotAgeDays ?? 'unknown' }} days</span>
          </div>
          <p class="modal-help" :data-testid="`${armingFund.toLowerCase()}-arming-runbook`">Runbook: {{ rampStatus.runbook }}</p>
        </template>
        <template v-else>
          <div class="modal-error" :data-testid="`${armingFund.toLowerCase()}-arming-error`">{{ rampError }}</div>
          <label class="modal-ack">
            <input
              type="checkbox"
              :data-testid="`${armingFund.toLowerCase()}-arming-ack-checkbox`"
              v-model="armAcknowledged"
            />
            <span>Enable anyway — ramp state unavailable. I understand I'm arming {{ armingFund }} without reviewing live ramp data.</span>
          </label>
        </template>

        <div class="modal-actions">
          <button type="button" class="btn-secondary-sm" :data-testid="`${armingFund.toLowerCase()}-arming-cancel`" @click="cancelArm">Cancel</button>
          <button type="button" class="btn-primary-sm" :data-testid="`${armingFund.toLowerCase()}-arming-confirm`" :disabled="armConfirmDisabled" @click="confirmArm">Enable {{ armingFund }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.alloc-card { border: 1px solid #333; border-radius: 4px; margin-bottom: 12px; }
.alloc-header { display: flex; align-items: center; justify-content: space-between; padding: 0.75rem 1rem; cursor: pointer; }
.alloc-header h3 { margin: 0; font-size: 0.9rem; color: #888; text-transform: uppercase; letter-spacing: 0.05em; }
.collapse-btn { background: none; border: none; color: #888; font-size: 1rem; cursor: pointer; padding: 0 0.25rem; }

.alloc-body { padding: 0 1rem 1rem; display: flex; flex-direction: column; gap: 0.6rem; }
.alloc-loading { color: #666; font-style: italic; font-size: 0.85rem; }

.alloc-row {
  display: flex; flex-wrap: wrap; align-items: center; gap: 0.6rem;
  padding: 0.5rem 0; border-bottom: 1px solid #2a2a2a;
}
.alloc-toggle-label { display: flex; align-items: center; gap: 0.4rem; min-width: 90px; }
.alloc-fund-name { font-weight: 600; color: #ccc; font-size: 0.85rem; }
.alloc-toggle-input { width: 20px; height: 20px; }
.alloc-toggle-input:disabled { opacity: 0.4; cursor: not-allowed; }

.alloc-weight-input {
  width: 70px; padding: 6px 8px; background: #222; color: #ccc;
  border: 1px solid #444; border-radius: 4px; font-size: 13px; min-height: 36px;
}
.alloc-weight-pct { color: #888; font-size: 0.8rem; }
.alloc-fund-name-readonly { min-width: 90px; }
.alloc-weight-readonly { color: #8b949e; font-size: 0.8rem; min-width: 40px; }

.alloc-status-chip {
  font-size: 0.72rem; padding: 2px 8px; border-radius: 10px;
  background: #21262d; color: #8b949e; text-transform: uppercase; letter-spacing: 0.03em;
}
.alloc-status-chip.status-executed { background: #1b3a1b; color: #4caf50; }
.alloc-status-chip.status-waiting, .alloc-status-chip.status-retrying { background: #3a2f1b; color: #ff9800; }
.alloc-status-chip.status-error, .alloc-status-chip.status-missed { background: #3a1b1b; color: #f44336; }


.btn-liquidate {
  margin-left: auto; padding: 6px 12px; background: transparent; color: #f44336;
  border: 1px solid #f44336; border-radius: 4px; font-size: 0.75rem; cursor: pointer; min-height: 36px;
}
.btn-liquidate:disabled { opacity: 0.35; cursor: not-allowed; }

.alloc-sum-row { display: flex; align-items: center; gap: 0.75rem; margin-top: 0.25rem; flex-wrap: wrap; }
.alloc-sum { font-size: 0.85rem; color: #ccc; }
.alloc-sum.invalid { color: #f44336; font-weight: 600; }
.btn-save { padding: 8px 16px; background: #4caf50; color: #fff; border: none; border-radius: 4px; font-weight: bold; cursor: pointer; min-height: 40px; }
.btn-save:disabled { opacity: 0.35; cursor: not-allowed; }
.saved-badge { font-size: 11px; color: #4caf50; padding: 2px 8px; background: #1b3a1b; border-radius: 3px; }

.alloc-error { padding: 8px 10px; background: #3a1b1b; border: 1px solid #f44336; border-radius: 4px; color: #f44336; font-size: 0.8rem; }
.alloc-liquidate-result { padding: 8px 10px; background: #12202e; border: 1px solid #2196f3; border-radius: 4px; color: #8bb; font-size: 0.8rem; }
.viewer-note { color: #8b949e; font-size: 0.85rem; font-style: italic; margin-top: 0.25rem; }

.alloc-pot-field { display: flex; align-items: center; gap: 0.3rem; }
.alloc-pot-label { color: #888; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.03em; }
.alloc-pot-input {
  width: 64px; padding: 6px 8px; background: #222; color: #ccc;
  border: 1px solid #444; border-radius: 4px; font-size: 13px; min-height: 36px;
}
.alloc-pot-error { color: #f44336; font-size: 0.8rem; }
.alloc-pot-hint { margin: 0; color: #8b949e; font-size: 0.78rem; line-height: 1.5; }

/* Task 8b: Tier/Reserve column legend + info panel — icon/panel styles
   match ExecPanel.vue's Auto-Execute info-icon/info-panel pattern. */
.alloc-pot-legend { display: flex; gap: 1.25rem; flex-wrap: wrap; font-size: 0.78rem; color: #888; text-transform: uppercase; letter-spacing: 0.03em; }
.alloc-pot-legend-item { display: inline-flex; align-items: center; }

.info-icon {
  display: inline-flex; align-items: center; justify-content: center;
  width: 16px; height: 16px; border-radius: 50%;
  background: #21262d; color: #8b949e; font-size: 0.6rem;
  cursor: pointer; font-style: italic; font-weight: 600;
  margin-left: 4px; vertical-align: middle;
}
.info-icon:hover { background: #30363d; color: #c9d1d9; }

.info-panel {
  background: #0d1117;
  border: 1px solid #21262d;
  border-radius: 6px;
  padding: 0.75rem 0.9rem;
  font-size: 0.8rem;
  color: #8b949e;
  line-height: 1.6;
}
.info-panel p { margin: 0 0 0.5rem 0; }
.info-panel strong { color: #c9d1d9; }
.info-panel ul, .info-panel ol { margin: 0 0 0.5rem 1.2rem; padding: 0; }
.info-panel li { margin-bottom: 0.25rem; }

.info-panel-table {
  width: 100%; border-collapse: collapse; margin: 0 0 0.75rem; font-size: 0.75rem;
  display: block; overflow-x: auto; white-space: normal;
}
.info-panel-table th, .info-panel-table td {
  border: 1px solid #21262d; padding: 0.4rem 0.5rem; text-align: left; vertical-align: top;
}
.info-panel-table th { color: #c9d1d9; font-weight: 600; }

/* Modal — matches AdminPanel.vue's manual-close modal pattern */
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(0, 0, 0, 0.6);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
}
.modal {
  background: #161b22; border: 1px solid #30363d; border-radius: 8px;
  padding: 1.25rem 1.5rem; width: min(480px, 90vw);
  display: flex; flex-direction: column; gap: 0.6rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}
.modal-title { margin: 0; font-size: 0.95rem; font-weight: 600; color: #e6edf3; }
.modal-help {
  margin: 0; font-size: 0.8rem; color: #8b949e; line-height: 1.5;
  border-left: 3px solid #d29922; padding-left: 0.6rem;
}
.modal-loading { color: #8b949e; font-size: 0.85rem; font-style: italic; }
.modal-field { display: flex; justify-content: space-between; font-size: 0.85rem; color: #c9d1d9; }
.modal-label { color: #6e7681; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.4px; }
.modal-error { color: #f85149; font-size: 0.82rem; }
.modal-ack { display: flex; align-items: flex-start; gap: 0.5rem; font-size: 0.78rem; color: #d29922; margin-top: 0.25rem; }
.modal-ack input { margin-top: 2px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 0.25rem; }
.btn-secondary-sm {
  padding: 5px 12px; background: transparent; border: 1px solid #58a6ff; border-radius: 4px;
  color: #58a6ff; font-size: 0.8rem; cursor: pointer; min-height: 36px;
}
.btn-primary-sm {
  padding: 5px 12px; background: #238636; border: 1px solid #2ea043; border-radius: 4px;
  color: #fff; font-size: 0.8rem; font-weight: 600; cursor: pointer; min-height: 36px;
}
.btn-primary-sm:disabled { opacity: 0.5; cursor: not-allowed; }

@media (max-width: 600px) {
  .alloc-row { flex-direction: column; align-items: stretch; }
  .btn-liquidate { margin-left: 0; }
  .alloc-pot-field { justify-content: space-between; }
  .alloc-pot-input { width: 96px; }
}
</style>
